// KS3 Student Book — Youth Programme (Ages 11–14)
// Phase 2 launch: 3 starter chapters with inquiry-led structure.
// Full 12-chapter expansion (Ethics & Society, Ambedkar: Justice & Reform,
// Mindfulness & Modern Life) to follow.

export const META = {
  series: "Ambedkarite Buddhist Educational Series — UK Edition",
  title: "Youth Programme · Key Stage 3",
  subtitle: "Ages 11–14 · Ethics, justice, mindfulness and civic responsibility",
  edition: "Phase 2 · Sample Edition · 2026",
  status: "3 chapters published · 9 more in development",
};

export const CHAPTERS = [
  {
    n: 1,
    title: "What is a Just Society?",
    bigIdea: "A just society isn't one without conflict — it's one where everybody has a fair chance.",
    vocab: [
      ["justice", "fairness in the way people are treated and the way decisions are made."],
      ["caste", "a social class one is born into, historically used in India to limit opportunity."],
      ["constitution", "the document that sets out the basic rights and rules of a country."],
      ["public benefit", "doing something that helps people beyond a private group."],
    ],
    storyTitle: "The Pupil Who Sat on the Floor",
    story: [
      "In the 1900s, in a town called Satara, a thirteen-year-old boy walked to school each morning with a piece of sacking under his arm.",
      "He was a brilliant student. His teachers said so. But because of the caste he was born into, he was not allowed to sit on the school bench. He sat on his sack, alone, at the back of the classroom.",
      "The boy's name was <strong>Bhimrao Ramji Ambedkar</strong>.",
      "He went on to read at Columbia University in New York, at the London School of Economics, and at Gray's Inn in London.",
      "He returned to India and helped write his country's <strong>Constitution</strong> — the rulebook that finally said: <em>every citizen is equal under the law.</em>",
      "He spent his life arguing one simple idea: a just society is one where the rules give every child a fair start, no matter who their parents are.",
    ],
    source: {
      quote: "\"I measure the progress of a community by the degree of progress which women have achieved.\"",
      attribution: "Dr B. R. Ambedkar",
    },
    activityTitle: "Map the Fairness in Your School",
    activity: [
      "In groups of three, list <strong>three</strong> rules in your school that exist to keep things fair.",
      "List <strong>three</strong> rules whose <em>purpose</em> you are not sure about.",
      "Discuss: which group of pupils benefits most from each rule? Does any rule unintentionally exclude anyone?",
      "Present one suggestion to the class for making one rule fairer.",
    ],
    discussionTitle: "Debate together",
    talkPrompts: [
      "Is a rule still fair if it treats everyone exactly the same — but some people start with less?",
      "If Ambedkar visited your school today, what one thing do you think he'd praise? What would he change?",
      "How is the freedom to learn linked to other freedoms (like the freedom to choose a job)?",
    ],
    writtenTask: "In 150 words, describe a time when you saw something <em>unfair</em> happen — at school, at home, or in the news. What made it unfair, and what would a 'just' response have looked like?",
  },
  {
    n: 2,
    title: "The Four Truths in Plain English",
    bigIdea: "The Buddha's teaching starts with an honest look at why life can be hard — then offers a practical way forward.",
    vocab: [
      ["dukkha", "a Pāli word usually translated as 'suffering' — but closer to 'unsatisfactoriness' or 'unease'."],
      ["mindfulness", "noticing what's happening, in the present moment, without judging it."],
      ["the Eightfold Path", "the Buddha's practical guide to living an ethical, balanced life."],
      ["compassion", "noticing another person's suffering and wanting to help."],
    ],
    storyTitle: "Why the Prince Left the Palace",
    story: [
      "Two and a half thousand years ago, in northern India, a prince named <strong>Siddhārtha</strong> grew up inside palace walls.",
      "His father had filled the palace with every comfort, hoping his son would never see hardship.",
      "But one day Siddhārtha rode beyond the walls. He saw an old person. He saw an ill person. He saw a funeral procession.",
      "He realised that no palace wall could keep these out forever.",
      "He left the palace to find a way through this honest fact of life — not by hiding from it, but by understanding it.",
      "Years later, sitting under a bodhi tree, he saw something quietly enormous: <strong>suffering has causes, and those causes can be untangled.</strong>",
      "He spent the rest of his life teaching this. He became known as the <strong>Buddha</strong> — 'the awakened one'.",
    ],
    source: {
      quote: "\"All conditioned things are impermanent. When one sees this with wisdom, one turns away from suffering.\"",
      attribution: "Dhammapada, verse 277",
    },
    activityTitle: "Map Your Own Four Truths",
    activity: [
      "Pick one small recurring frustration in your life (a class you struggle in, a friendship tension, a worry).",
      "Truth 1 — Name it: in one sentence, what is the difficulty?",
      "Truth 2 — Trace it: what causes it? (Be honest, not blaming.)",
      "Truth 3 — Imagine it eased: what would it look like if the cause shifted?",
      "Truth 4 — Plan a small action: name <strong>one</strong> thing you could try this week.",
    ],
    talkPrompts: [
      "Why might 'unease' be a more useful translation of dukkha than 'suffering'?",
      "Where in modern life do you see people trying to live inside 'palace walls' — and what does that cost them?",
      "Is the Buddha's approach religious, philosophical, or practical? Can it be all three?",
    ],
    writtenTask: "Write a one-page reflection (200 words) on a moment this week when you used a 'four-truths' style of thinking — even informally — to deal with a small problem.",
  },
  {
    n: 3,
    title: "Voice, Vote, Voice Again",
    bigIdea: "In a democracy, your voice is your share of power — and using it well takes practice.",
    vocab: [
      ["democracy", "a system where the people choose how they are governed."],
      ["franchise", "the right to vote."],
      ["civic", "to do with our shared life as citizens — schools, councils, charities, parliaments."],
      ["trustee", "a person who looks after a charity's mission and money on behalf of the public."],
    ],
    storyTitle: "The Long Walk to the Ballot Box",
    story: [
      "Until 1928, women in the UK under the age of 30 could not vote. Until 1918, no woman could vote at all, and many working men could not either.",
      "In India, when independence came in 1947, the founders made a brave decision: every adult citizen, woman or man, rich or poor, would have the vote from day one.",
      "The man who drafted that section of the Indian Constitution was a former classroom-sack pupil from Satara — <strong>Dr B. R. Ambedkar</strong>.",
      "He believed the vote was meaningless without two other things: <strong>education</strong> (so you can think for yourself) and <strong>organisation</strong> (so a single voice becomes many).",
      "He gave a famous instruction to a generation: <em>'Educate, Agitate, Organise.'</em>",
      "Modern democracy doesn't end at the ballot box. Charities, community groups, school councils and trustees are all small democracies. They need our voices too.",
    ],
    source: {
      quote: "\"Democracy is not merely a form of government. It is primarily a mode of associated living, of conjoint communicated experience.\"",
      attribution: "Dr B. R. Ambedkar, 1949 speech",
    },
    activityTitle: "Charity Trustee Role-Play",
    activity: [
      "In groups of five, you are the trustees of an imaginary community Sangha with £5,000 in the bank.",
      "Three proposals are on the table: (a) a weekend retreat for elders, (b) a workbook print run for a local school, (c) a youth meditation evening.",
      "Each member argues for one option. Vote in two rounds — first by hand, then by secret ballot.",
      "Did the result change between the two rounds? Why?",
    ],
    talkPrompts: [
      "Is voting enough? What else does a strong democracy need?",
      "What's the difference between 'agitating' and being rude?",
      "Where in your life right now do you already have a vote (literal or informal)?",
    ],
    writtenTask: "Write a 200-word letter to your future 18-year-old self, persuading them to vote in their first election — and to keep using their 'civic voice' beyond it.",
  },
];
